import { useEffect } from 'react';
import { Wordmark } from './ui';

// Marketing landing page served at /app. Linked from Apple App Store
// Connect's "Marketing URL" field — Apple Review prefers a real
// marketing destination over the homepage when available, and a clean
// landing page improves Search Ads conversion. Web visitors who land
// here from non-game contexts (a podcast mention, a social-media
// share, etc.) get a focused pitch + dual CTA (App Store / play in
// browser) instead of being dropped straight into a puzzle.

// Canonical App Store listing URL — pre-resolved country (`/us/`) +
// SEO slug so clicks skip Apple's redirect chain. The only authoritative
// piece is `id6766731447`; the rest of the path is forgiving.
const APP_STORE_URL = 'https://apps.apple.com/us/app/hexponents-daily-math-puzzle/id6766731447';

const COMPANY = 'DosSantos Systems LLC';
const YEAR = new Date().getFullYear();

export function MarketingLanding() {
  useEffect(() => {
    document.title = 'Hexponents · Daily logic puzzle on a hex grid';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="hm-marketing">
      <header className="hm-marketing-header">
        <a href="/" className="hm-marketing-home" aria-label="Hexponents home">
          <Wordmark />
        </a>
        <nav className="hm-marketing-nav">
          <a href="/">Play in browser</a>
          <a href="/support">Support</a>
        </nav>
      </header>

      <main className="hm-marketing-main">
        {/* Hero — the above-fold sales pitch */}
        <section className="hm-marketing-hero">
          <div className="hm-marketing-hero-copy">
            <p className="hm-marketing-eyebrow">Daily logic puzzle</p>
            <h1 className="hm-marketing-headline">
              Solve the hex.
            </h1>
            <p className="hm-marketing-sub">
              A new puzzle every day on a hexagonal grid. Fit every tile so
              every line — sum, product, parity, range — tells the truth.
              A five-minute daily ritual that scales to expert-level
              satisfaction.
            </p>
            <div className="hm-marketing-cta">
              <a
                className="hm-marketing-cta-primary"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {/* Apple wordmark — minimal SVG to avoid bundling Apple's
                    full badge image while staying within their guideline
                    that branding link to the App Store. */}
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  style={{ flex: 'none' }}
                >
                  <path
                    fill="currentColor"
                    d="M16.365 1.43c0 1.14-.49 2.27-1.27 3.06-.85.86-2.21 1.51-3.36 1.43-.13-1.13.4-2.34 1.16-3.13.85-.89 2.31-1.55 3.47-1.36zm3.86 17.65c-.78 1.78-1.16 2.58-2.16 4.16-.88 1.39-2.12 3.13-3.66 3.14-1.37.02-1.72-.89-3.58-.88-1.86.01-2.25.9-3.62.88-1.54-.02-2.71-1.58-3.59-2.97C2.07 19.16.74 14.7 2.86 11.62c1.5-2.18 3.87-3.46 6.1-3.46 1.39 0 2.7.51 3.84.51 1.13 0 2.7-.62 4.31-.53 1.39.07 3.41.5 4.7 2.43-3.97 2.18-3.32 7.79.42 9.51z"
                  />
                </svg>
                <span>
                  <small>Download on the</small>
                  <strong>App Store</strong>
                </span>
              </a>
              <a className="hm-marketing-cta-secondary" href="/">
                Play in browser
              </a>
            </div>
            <p className="hm-marketing-fineprint">
              Free to play. Premium $1.99/month or $19.99/year unlocks the
              archive and custom-puzzle designer.
            </p>
          </div>
          <div className="hm-marketing-hero-art">
            <img
              src="/marketing/hero-hook.png"
              alt="Hexponents board mid-game on iPhone"
              loading="eager"
            />
          </div>
        </section>

        {/* Three-up feature grid */}
        <section className="hm-marketing-features">
          <article className="hm-marketing-feature">
            <img
              src="/marketing/feature-mechanic.png"
              alt="Hexponents board showing every line constraint passing"
              loading="lazy"
            />
            <h2>Every line tells the truth</h2>
            <p>
              Sums, products, parity, range, contains-set. Ten constraint
              types you'll learn by playing, not by reading a manual.
              Place every tile so every line's constraint holds.
            </p>
          </article>
          <article className="hm-marketing-feature">
            <img
              src="/marketing/feature-streaks.png"
              alt="Hexponents profile showing a streak counter"
              loading="lazy"
            />
            <h2>Build the streak</h2>
            <p>
              A flame for every day you show up. Streaks count days
              played, not days solved — open the app, change a tile, the
              day counts. Sync across web and iOS.
            </p>
          </article>
          <article className="hm-marketing-feature">
            <img
              src="/marketing/feature-badges.png"
              alt="Hexponents badge gallery"
              loading="lazy"
            />
            <h2>Fifty badges to chase</h2>
            <p>
              From sub-30s Easy speed runs to The Completionist (every
              puzzle since launch, ever). All-Rounder for clearing every
              difficulty in a single day. Hidden goals to discover.
            </p>
          </article>
        </section>

        {/* Below-the-fold callouts */}
        <section className="hm-marketing-callout">
          <div className="hm-marketing-callout-copy">
            <p className="hm-marketing-eyebrow">Premium</p>
            <h2>Build your own.</h2>
            <p>
              Premium subscribers get the full puzzle archive, an offline
              downloader, and the in-app custom puzzle designer. Every
              published puzzle is solver-verified before it ships — if it's
              broken, it can't go live. Share via a link that opens
              directly in the app for everyone, even non-Premium players.
            </p>
            <a className="hm-marketing-cta-secondary" href="/browse">
              Browse community puzzles
            </a>
          </div>
          <div className="hm-marketing-callout-art">
            <img
              src="/marketing/feature-cta.png"
              alt="Hexponents home screen — today's puzzle"
              loading="lazy"
            />
          </div>
        </section>

        {/* Final CTA */}
        <section className="hm-marketing-final">
          <h2>Today's puzzle is live.</h2>
          <p>Free to play. Five minutes. See you tomorrow.</p>
          <div className="hm-marketing-cta">
            <a
              className="hm-marketing-cta-primary"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" style={{ flex: 'none' }}>
                <path
                  fill="currentColor"
                  d="M16.365 1.43c0 1.14-.49 2.27-1.27 3.06-.85.86-2.21 1.51-3.36 1.43-.13-1.13.4-2.34 1.16-3.13.85-.89 2.31-1.55 3.47-1.36zm3.86 17.65c-.78 1.78-1.16 2.58-2.16 4.16-.88 1.39-2.12 3.13-3.66 3.14-1.37.02-1.72-.89-3.58-.88-1.86.01-2.25.9-3.62.88-1.54-.02-2.71-1.58-3.59-2.97C2.07 19.16.74 14.7 2.86 11.62c1.5-2.18 3.87-3.46 6.1-3.46 1.39 0 2.7.51 3.84.51 1.13 0 2.7-.62 4.31-.53 1.39.07 3.41.5 4.7 2.43-3.97 2.18-3.32 7.79.42 9.51z"
                />
              </svg>
              <span>
                <small>Download on the</small>
                <strong>App Store</strong>
              </span>
            </a>
            <a className="hm-marketing-cta-secondary" href="/">
              Play in browser
            </a>
          </div>
        </section>
      </main>

      <footer className="hm-marketing-footer">
        <p>© {YEAR} {COMPANY}. All rights reserved.</p>
        <nav>
          <a href="/">Home</a>
          {' · '}
          <a href="/support">Support</a>
          {' · '}
          <a href="/privacy">Privacy</a>
          {' · '}
          <a href="/terms">Terms</a>
          {' · '}
          <a href="/copyright">Copyright</a>
        </nav>
      </footer>
    </div>
  );
}
